import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import { closeModal } from "store/modals/slice";
import { SegmentItem } from "store/segments/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import { Modal } from "components/Modal/Modal";
import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import styles from "./NotificationSendModal.module.scss";

const NotificationSendModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isFetched, setFetched] = useState<boolean>(false);
  const [notificationsList, setNotificationsList] = useState<SegmentItem[]>([]);
  const [notificationsListFull, setNotificationsListFull] = useState<
    SegmentItem[]
  >([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedNotif, setSelectedNotif] = useState<number>();
  const selectedItems = useAppSelector((store) => store.players.selectedItems);

  const handleLoadNotifications = async () => {
    setFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.notificationsList}?limit=1000&offset=0`,
      );
      setNotificationsListFull(result.data.items);
    } catch {
      console.log("handleLoadNotifications error");
    } finally {
      setFetched(true);
    }
    setFetched(true);
  };

  useEffect(() => {
    handleLoadNotifications();
  }, []);

  useEffect(() => {
    setNotificationsList(
      notificationsListFull.filter((el) =>
        el.title?.toLowerCase().includes(searchValue?.toLowerCase()),
      ),
    );
  }, [notificationsListFull, searchValue]);

  const handleCloseClick = () => {
    navigate(`${location.pathname}`);
    dispatch(closeModal());
  };

  const handleApply = async () => {
    try {
      await instance.put(`${apiRoutes.notificatioSend}/${selectedNotif}`, {
        userIds: selectedItems,
      });
      dispatch(
        showNotification({
          title: t("success"),
          message: t("notifSendSuccess"),
          type: NotificationTypes.success,
        }),
      );
      handleCloseClick();
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("notifSendError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const handleItemClick = (id: number) => {
    if (selectedNotif === id) {
      setSelectedNotif(undefined);
    } else {
      setSelectedNotif(id);
    }
  };

  return (
    <Modal onClose={handleCloseClick}>
      <Modal.Header />

      {!isFetched ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Content className={styles.content}>
            <div className="games-order-search">
              <SearchInput
                placeholder={t("searchNotification")}
                value={searchValue}
                onChange={onSearch}
                isSmall
              />
            </div>

            <div
              className={`games-order-list segments-order-list ${styles.notificationsList}`}
            >
              <div className="item">
                <div className="id">ID</div>
                <div className="name">{t("name").toUpperCase()}</div>
                <div className="type">{t("type").toUpperCase()}</div>
                <div className="active">{t("active").toUpperCase()}</div>
              </div>

              {notificationsList.length === 0 ? (
                <div className={styles.empty}>
                  {t("noNotificationResultsFound")}
                </div>
              ) : (
                notificationsList.map((el) => (
                  <div
                    className={`item ${selectedNotif === el.id ? "active" : ""}`}
                    key={el.id}
                    onClick={() => handleItemClick(el.id ?? 0)}
                  >
                    <div className="id">{el.id}</div>
                    <div className="name">{el.title}</div>
                    <div className="type">{t(el.type)}</div>
                    <div className="active">
                      <div
                        className={`active-label ${el.isActive ? "active" : ""}`}
                      >
                        {el.isActive ? "On" : "Off"}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Modal.Content>

          <Modal.Footer>
            <Button
              isSecondary
              txt={t("cancel")}
              handleClick={handleCloseClick}
            />
            <Button
              isPrimary
              handleClick={handleApply}
              txt={t("send")}
              disabled={!selectedNotif}
            />
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default NotificationSendModal;
